import type { SampleReport } from '@ctcells/contract'

import type { StageVerdictLookup } from './sampleFlowModel.ts'
import { stageEventsSince } from './stageTimes.ts'

/**
 * **시료 × 단계의 판정** — `GET /reports/{sample_id}.events[].error_code` **하나만** 읽는다.
 *
 * ## 🔴 왜 임시 목을 걷었나 (2026-09-22 · eric 「너 추천대로 해」 → 팀장 결정)
 *
 * 2026-09-21 에 `temporaryStageVerdicts.ts` 가 시료 ID 해시로 실패를 만들어 그렸다. 그때는
 * 「빈 칸이 가득한 뼈대」를 메우려던 것이고 **실물이 아직 끝까지 안 돌았다.** 2026-09-22 에
 * 레시피 `fe-20260922-full-r1` 로 원심→어셈블→염색→완료를 **두 번 완주**시키자 판단 재료가
 * 달라졌다 — 실측(2회차 염색 진행 중):
 *
 * | 화면이 말한 것 | 서버가 말한 것 |
 * | --- | --- |
 * | 원심 「실패 1건」 · 어셈블 「실패 2건」 | 그 시료 5건 보고서 **`error_code` 전건 `null`** |
 * | P2 · P4 · REF 챔버에 빨간 테두리 | 셋 다 `stage: staining` — 서버는 실패를 한 자도 안 말했다 |
 *
 * 🔴 **회차가 앞으로 갈수록 번진다.** 판정이 「도는 장비가 지난 단계」에 붙으므로 염색 시점엔
 *    5건 중 3건이 실패로 섰다. 고객에게 나갈 화면이 **없는 실패를 단언**하는 것이고,
 *    CLAUDE.md 의 「‘못 봤다’를 ‘통과’로 적지 마라」와 **같은 부류의 거짓**이다(방향만 반대).
 *
 * ## 이 파일이 말하는 것 — **실패 하나뿐이다**
 *
 * 🔴 **`error_code` 가 실린 사건만 실패다.** 그 칸이 `null`·빈 문자열이거나 키가 없으면
 *    **아무 말도 하지 않는다**(`undefined`). 그러면 칸은 종전대로 「지남 / 대기」다.
 * 🔴 **「성공」을 만들지 않는다.** 단계 표지가 찍혔다는 것과 그 단계가 성공했다는 것은 다른
 *    사실이다. 지금 서버의 insert 는 `error_code` 를 **한 번도 안 쓴다**
 *    (`app/services/sample_lifecycle.py:152` 의 여덟 칸에 그 이름이 없다 · BR-49) — 그러니
 *    `null` 은 「성공」이 아니라 **「아무 말도 안 했다」**다. 그것을 성공으로 접는 것이 바로
 *    이 코드베이스가 `verdict: null` 을 `'pass'` 로 접던 사고다.
 *    ⇒ 서버가 **명시적 성공 판정**을 싣기 시작하는 날 이 함수에 그 갈래가 붙는다. 그때까지는
 *      `StageVerdict` 의 `'pass'` 가 **아무 데서도 나오지 않는다**(어휘는 남겨 둔다 —
 *      `flowVocabulary.flowStateText` 가 그날 바로 받을 자리다).
 *
 * ## 길은 열려 있다 — 값이 오면 **그대로 선다**
 *
 * 서버가 `error_code` 를 싣기 시작하면 코드 수정이 **한 줄도 필요 없다.** 그 자리에 실패가
 * 서고 사유는 카탈로그 문구(`errorMessage`)로 나간다. `stageVerdicts.test.ts` 가 두 방향을
 * 같이 잰다 — ⓐ 전건 `null` 이면 판정 0건 ⓑ 코드가 실리면 그 단계가 실패.
 *
 * ## 회차 창 — 시각과 **같은 거르기**를 쓴다
 *
 * 보고서 `events` 가 `batch_id` 로 걸러지지 않아 **어제 회차의 사건이 섞여 온다**
 * (`stageTimes.ts` 머리말 · BR). 판정도 같은 창(`since = batch.started_at`)을 지난다 —
 * 두 축이 다른 창을 쓰면 「시각은 이번 회차인데 실패는 어제 것」이 된다.
 */
export function stageVerdictLookup(
  reports: ReadonlyMap<string, Pick<SampleReport, 'events'>>,
  since: string | undefined,
): StageVerdictLookup {
  /*
   * 🔴 **한 번만 훑는다.** `flowRows` 가 시료×단계마다 부르므로(9×5 = 45회) 그때마다
   *    `events` 를 다시 훑으면 같은 일을 마흔다섯 번 한다.
   */
  const failures = new Map<string, string>()
  for (const [sampleId, report] of reports) {
    for (const event of stageEventsSince(report, since)) {
      if (event.errorCode === undefined) continue
      /* 같은 단계가 두 번 오면 **나중** 것이다 — `stageTimes` 와 같은 규칙 */
      failures.set(`${sampleId}|${event.stage}`, event.errorCode)
    }
  }

  return (sampleId, stage) => {
    const errorCode = failures.get(`${sampleId}|${stage}`)
    return errorCode === undefined ? undefined : { verdict: 'fail', errorCode }
  }
}
